import { isNull, isNumber, isString, isUndefined } from '@fmnts/core';
import * as Array from 'effect/Array';
import * as Brand from 'effect/Brand';
import * as Either from 'effect/Either';
import { flow } from 'effect/Function';
import * as Option from 'effect/Option';
import * as Predicate from 'effect/Predicate';
import * as ParseResult from './parse-result';

/**
 * A codec is used to decode / encode a data type.
 */
export interface Codec<A, I> {
  /** Decodes an input `i`. */
  decode: (i: I) => Either.Either<A, ParseResult.ParseIssue>;
  /** Decodes a type `a`. */
  encode: (a: A) => Either.Either<I, ParseResult.ParseIssue>;
}

/** Describes an arbitrary codec. */
export type Any = Codec<any, any>;
/** Helper type to get the decoded type of a codec. */
export type Decoded<C extends Any> = Parameters<C['encode']>[0];
/** Helper type to get the encoded type of a codec. */
export type Encoded<C extends Any> = Parameters<C['decode']>[0];

/** A decode operation. */
type DecodeUnknown<Out> = (
  u: unknown,
) => Either.Either<Out, ParseResult.ParseIssue>;

// eslint-disable-next-line @typescript-eslint/naming-convention
export type transformOrFail<From extends Any, To extends Any> = Codec<
  Decoded<To>,
  Encoded<From>
>;
export const transformOrFail: <To extends Any, From extends Any>(
  from: From,
  to: To,
  options: {
    readonly decode: (
      fromA: Decoded<From>,
    ) => Either.Either<Encoded<To>, ParseResult.ParseIssue>;
    readonly encode: (
      toI: Encoded<To>,
    ) => Either.Either<Decoded<From>, ParseResult.ParseIssue>;
  },
) => transformOrFail<From, To> = <FromA, FromI, ToA, ToI>(
  from: Codec<FromA, FromI>,
  to: Codec<ToA, ToI>,
  options: {
    readonly decode: (
      fromA: FromA,
    ) => Either.Either<ToI, ParseResult.ParseIssue>;
    readonly encode: (toI: ToI) => Either.Either<FromA, ParseResult.ParseIssue>;
  },
): Codec<ToA, FromI> => ({
  decode: (i) => {
    const fromA = from.decode(i);
    if (Either.isLeft(fromA)) {
      return Either.left(fromA.left);
    }
    const toI = options.decode(fromA.right);
    if (Either.isLeft(toI)) {
      return Either.left(toI.left);
    }
    return to.decode(toI.right);
  },
  encode: (a) => {
    const toI = to.encode(a);
    if (Either.isLeft(toI)) {
      return Either.left(toI.left);
    }
    const fromA = options.encode(toI.right);
    if (Either.isLeft(fromA)) {
      return Either.left(fromA.left);
    }
    return from.encode(fromA.right);
  },
});

// eslint-disable-next-line @typescript-eslint/naming-convention
export type transform<From extends Any, To extends Any> = transformOrFail<
  From,
  To
>;

/**
 * Create a new `Schema` by transforming the input and output of an existing `Schema`
 * using the provided mapping functions.
 */
export const transform: <To extends Any, From extends Any>(
  from: From,
  to: To,
  options: {
    readonly decode: (fromA: Decoded<From>) => Encoded<To>;
    readonly encode: (toI: Encoded<To>) => Decoded<From>;
  },
) => transform<From, To> = <FromA, FromI, ToA, ToI>(
  from: Codec<FromA, FromI>,
  to: Codec<ToA, ToI>,
  options: {
    readonly decode: (fromA: FromA) => ToI;
    readonly encode: (toI: ToI) => FromA;
  },
) =>
  transformOrFail(from, to, {
    decode: (fromA) => ParseResult.succeed(options.decode(fromA)),
    encode: (toI) => ParseResult.succeed(options.encode(toI)),
  });

/**
 * Codec: Unknown <-> Unkown.
 */
export const Unknown: Codec<unknown, unknown> = {
  decode: ParseResult.succeed,
  encode: ParseResult.succeed,
};
const decodeStringFromString: DecodeUnknown<string> = (i) =>
  isString(i)
    ? Either.right(i)
    : Either.left(new ParseResult.Type(i, 'Expected: string'));

/**
 * Codec: String <-> Unkown.
 */
const StringUnknown: Codec<string, unknown> = {
  decode: decodeStringFromString,
  encode: decodeStringFromString,
};
/**
 * Codec: String <-> String.
 */
const String$: Codec<string, string> = StringUnknown as Codec<string, string>;

const decodeNumberFromNumber: DecodeUnknown<number> = (i) =>
  isNumber(i)
    ? !Number.isNaN(i)
      ? Either.right(i)
      : Either.left(new ParseResult.Type(i, 'Expected: number'))
    : Either.left(new ParseResult.Type(i, 'Expected: number'));

const decodeNumberFromString = (
  a: string,
): Either.Either<number, ParseResult.ParseIssue> => {
  const parsed = Number.parseFloat(a);
  return isNaN(parsed)
    ? Either.left(new ParseResult.Transformation(a, 'Expected: Numeric string'))
    : Either.right(parsed);
};

const encodeNumberAsString = (a: number): Either.Either<string, never> =>
  Either.right(`${a}`);

/**
 * Codec: Number <-> Unknown.
 */
const NumberUnknown: Codec<number, unknown> = {
  decode: decodeNumberFromNumber,
  encode: decodeNumberFromNumber,
};
/**
 * Codec: Number <-> Number.
 */
const Number$: Codec<number, number> = NumberUnknown as Codec<number, number>;

/**
 * Codec: Number <-> Number.
 */
export const NumberFromString: Codec<number, string> = {
  decode: flow(String$.decode, Either.flatMap(decodeNumberFromString)),
  encode: flow(Number$.encode, Either.flatMap(encodeNumberAsString)),
};

const decodeBooleanFromBoolean: DecodeUnknown<boolean> = (i) =>
  Predicate.isBoolean(i)
    ? Either.right(i)
    : Either.left(new ParseResult.Type(i, 'Expected: boolean'));

const decodeBooleanFromString = (
  a: string,
): Either.Either<boolean, ParseResult.ParseIssue> => {
  switch (a) {
    case 'true':
      return Either.right(true);
    case 'false':
      return Either.right(false);
    default:
      return Either.left(
        new ParseResult.Transformation(a, 'Must be true / false'),
      );
  }
};

const encodeBooleanAsString = (
  a: boolean,
): Either.Either<'true' | 'false', never> => Either.right(a ? 'true' : 'false');
/**
 * Codec: Boolean <-> Boolean.
 */
const Boolean$: Codec<boolean, boolean> = {
  decode: decodeBooleanFromBoolean,
  encode: decodeBooleanFromBoolean,
};

/**
 * Codec: Boolean <-> String
 */
export const BooleanFromString: Codec<boolean, string> = {
  decode: flow(String$.decode, Either.flatMap(decodeBooleanFromString)),
  encode: flow(Boolean$.encode, Either.flatMap(encodeBooleanAsString)),
};

export type BrandSchema<A extends Brand.Brand<any>, I = A> = Codec<A, I>;
/**
 * Codec from a branded type.
 * @param brand Brand constructur
 * @returns
 */
export const fromBrand =
  <C extends Brand.Brand<string | symbol>, A extends Brand.Brand.Unbranded<C>>(
    brand: Brand.Brand.Constructor<C>,
  ) =>
  <I>(self: Codec<A, I>): BrandSchema<C, I> => {
    const predicate = (a: A): Option.Option<ParseResult.ParseIssue> => {
      const either = brand.either(a);
      return Either.isLeft(either)
        ? Option.some(
            new ParseResult.Type(
              a,
              either.left.map((v) => v.message).join(', '),
            ),
          )
        : Option.none();
    };

    return {
      decode(i) {
        const a = self.decode(i);
        return Either.isLeft(a)
          ? ParseResult.fail(a.left)
          : Option.match(predicate(a.right), {
              onNone: () => ParseResult.succeed(brand(a.right)),
              onSome: (e) =>
                ParseResult.fail(
                  new ParseResult.Refinement(a.right, 'Predicate', e),
                ),
            });
      },
      encode(a) {
        const x = a as unknown as A;
        if (brand.is(x)) {
          return self.encode(x);
        }

        return ParseResult.fail(
          new ParseResult.Type(a, `Expected: Brand<${brand.name}>`),
        );
      },
    };
  };

export type TupleType<Elements extends ReadonlyArray<Any>> = Codec<
  { readonly [P in keyof Elements]: Decoded<Elements[P]> },
  { readonly [P in keyof Elements]: Encoded<Elements[P]> }
>;
export type ArrayType<Elements extends ReadonlyArray<Any>> = Codec<
  ReadonlyArray<Decoded<Elements[number]>>,
  ReadonlyArray<Encoded<Elements[number]>>
>;

/**
 * Tuple codec.
 */
export function Tuple<C extends ReadonlyArray<Any>>(
  ...codecs: [...C]
): TupleType<[...C]>;
export function Tuple(...codecs: Any[]): TupleType<Any[]> {
  const mapAll =
    <Out>(decode: ReadonlyArray<DecodeUnknown<Out>>) =>
    (v: ReadonlyArray<Out>) =>
      !Array.isArray(v)
        ? ParseResult.fail(new ParseResult.Type(v, 'Expected: Tuple'))
        : v.length < codecs.length
          ? ParseResult.fail(
              new ParseResult.Missing(
                v,
                `Expected ${codecs.length}, actual ${v.length}`,
              ),
            )
          : v.length > codecs.length
            ? ParseResult.fail(
                new ParseResult.Unexpected(
                  v,
                  `Expected ${codecs.length}, actual ${v.length}`,
                ),
              )
            : Either.all(v.map((x, index) => decode[index](x)));

  return {
    decode: mapAll(codecs.map((c) => c.decode)),
    encode: (a) =>
      Either.all(codecs.map((codec, index) => codec.encode(a[index]))),
  };
}

/**
 * Array codec.
 */
export function Array$<C extends Any>(codec: C): TupleType<ReadonlyArray<C>> {
  const mapAll =
    <Out>(decode: DecodeUnknown<Out>) =>
    (v: ReadonlyArray<Out>) =>
      Array.isArray(v)
        ? Either.all(v.map(decode))
        : ParseResult.fail(new ParseResult.Type(v, 'Expected: Array'));

  return {
    decode: mapAll(codec.decode),
    encode: mapAll(codec.encode),
  };
}

/**
 * Creates a union codec.
 */
export type Union<Members extends ReadonlyArray<Any>> = Codec<
  Decoded<Members[number]>,
  Encoded<Members[number]>
>;
export function Union(): never;
export function Union<A, B, I>(
  c1: Codec<A, I>,
  c2: Codec<B, I>,
): Codec<A | B, I>;
export function Union<Members extends Array.NonEmptyReadonlyArray<Any>>(
  ...members: Members
): Codec<Decoded<Members[number]>, Encoded<Members[number]>>;
export function Union<Members extends Array.NonEmptyReadonlyArray<Any>>(
  ...members: Members
): Codec<Decoded<Members[number]>, Encoded<Members[number]>> {
  const compositeDecode =
    <Out>(decodes: DecodeUnknown<Out>[]): DecodeUnknown<Out> =>
    (i) => {
      const issues: ParseResult.ParseIssue[] = [];
      for (const d of decodes) {
        const a = d(i);
        if (Either.isRight(a)) {
          return a;
        }
        issues.push(a.left);
      }

      return ParseResult.fail(
        Array.match(issues, {
          onEmpty: () =>
            new ParseResult.Missing(i, 'At least 1 member required.'),
          onNonEmpty: (x) => new ParseResult.Composite(i, x),
        }),
      );
    };

  return {
    decode: compositeDecode(members.map((m) => m.decode)),
    encode: compositeDecode(members.map((m) => m.encode)),
  };
}

const decodeNull: DecodeUnknown<null> = (i) =>
  isNull(i)
    ? ParseResult.succeed(null)
    : ParseResult.fail(new ParseResult.Type(i, 'Expected: null'));
const decodeUndefined: DecodeUnknown<undefined> = (i) =>
  isUndefined(i)
    ? ParseResult.succeed(undefined)
    : ParseResult.fail(new ParseResult.Type(i, 'Expected: undefined'));

export const NullUnknown: Codec<null, unknown> = {
  decode: decodeNull,
  encode: decodeNull,
};
/** Codec: null <-> null. */
export const Null: Codec<null, null> = NullUnknown as Codec<null, null>;
export const UndefinedUnknown: Codec<undefined, unknown> = {
  decode: decodeUndefined,
  encode: decodeUndefined,
};
/** Codec: undefined <-> undefined. */
export const Undefined: Codec<undefined, undefined> = UndefinedUnknown as Codec<
  undefined,
  undefined
>;

export type NullOr<S extends Any> = Union<[S, typeof Null]>;
export const NullOr = <S extends Any>(self: S): NullOr<S> => Union(self, Null);

export type UndefinedOr<S extends Any> = Union<[S, typeof Undefined]>;
export const UndefinedOr = <S extends Any>(self: S): UndefinedOr<S> =>
  Union(self, Undefined);

export type NullishOr<S extends Any> = Union<
  [S, typeof Null, typeof Undefined]
>;
export const NullishOr = <S extends Any>(self: S): NullishOr<S> =>
  Union(self, Null, Undefined);

export type OptionFromNullOr<Value extends Any> = Codec<
  Option.Option<Decoded<Value>>,
  Encoded<Value> | null
>;

export const OptionFromNullOr = <Value extends Any>(
  value: Value,
): OptionFromNullOr<Value> =>
  transform(NullOr(value), OptionFromSelf(value), {
    decode: Option.fromNullable,
    encode: Option.getOrNull,
  });

export type OptionFromNullishOr<Value extends Any> = Codec<
  Option.Option<Decoded<Value>>,
  Encoded<Value> | null | undefined
>;
/**
 * Creates a codec to transform nullable values and Option.
 *
 * @param value Value codec
 * @param onNoneEncoding Value to use to encode Option.none()
 *
 * @returns
 * Codec to handle nullable values and Option.
 */
export const OptionFromNullishOr = <Value extends Any>(
  value: Value,
  onNoneEncoding: null | undefined,
): OptionFromNullishOr<Value> =>
  transform(NullishOr(value), OptionFromSelf(value), {
    decode: Option.fromNullable,
    encode: onNoneEncoding === null ? Option.getOrNull : Option.getOrUndefined,
  });

export type OptionFromSelf<Value extends Any> = Codec<
  Option.Option<Decoded<Value>>,
  Option.Option<Encoded<Value>>
>;
/**
 * Creates a codec to handle a value as Option.some().
 *
 * @param codec Value codec.
 *
 * @returns
 */
export const OptionFromSelf = <C extends Any>(codec: C): OptionFromSelf<C> => ({
  decode: (value) => optionParse(codec.decode)(value),
  encode: (value) => optionParse(codec.encode)(value),
});

const optionParse =
  <A>(decodeUnknown: DecodeUnknown<A>): DecodeUnknown<Option.Option<A>> =>
  (u) =>
    Option.isOption(u)
      ? Option.isNone(u)
        ? ParseResult.succeed(Option.none())
        : toComposite(decodeUnknown(u.value), Option.some, u)
      : ParseResult.fail(new ParseResult.Type(u));

const toComposite = <A, B>(
  eff: Either.Either<A, ParseResult.ParseIssue>,
  onSuccess: (a: A) => B,
  actual: unknown,
): Either.Either<B, ParseResult.Composite> =>
  ParseResult.mapBoth(eff, {
    onFailure: (e) => new ParseResult.Composite(actual, [e]),
    onSuccess,
  });

/**
 * Creates a Codec for a record type.
 * @param keyCodec Codec used for keys.
 * @param valueCodec Codec used for values.
 *
 * @returns
 * A Codec for Records.
 */
export function Record<AK extends number | string, IK extends string, AV, IV>(
  keyCodec: Codec<AK, IK>,
  valueCodec: Codec<AV, IV>,
): Codec<Record<AK, AV>, Record<IK, IV>> {
  const mapRecord =
    <KOut extends number | string, VOut>(
      decodeKey: DecodeUnknown<KOut>,
      decodeValue: DecodeUnknown<VOut>,
    ): DecodeUnknown<Record<KOut, VOut>> =>
    (i) => {
      if (!Predicate.isRecord(i)) {
        return ParseResult.fail(new ParseResult.Type(i, 'Expected: Object'));
      }

      const decoded: Either.Either<[KOut, VOut], ParseResult.ParseIssue>[] = (
        Object.entries(i) as [unknown, unknown][]
      ).map(([ik, iv]) => Either.all([decodeKey(ik), decodeValue(iv)]));

      return Either.map(
        Either.all(decoded),
        (entries) => Object.fromEntries(entries) as Record<KOut, VOut>,
      );
    };
  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore IK not assignable to KOut, but thats what the key codec should check
    decode: mapRecord(keyCodec.decode, valueCodec.decode),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore AK not assignable to KOut, but thats what the key codec should check
    encode: mapRecord(keyCodec.encode, valueCodec.encode),
  };
}

/**
 * Creates a decoder using the provided codec
 *
 * @param codec Codec used for decoding
 */
export const decodeEither: <A, I>(
  codec: Codec<A, I>,
) => (i: I) => Either.Either<A, ParseResult.ParseError> = (codec) => (i) =>
  Either.mapLeft(codec.decode(i), ParseResult.parseError);

/**
 * Creates a decoder using the provided codec
 *
 * @param codec Codec used for decoding
 */
export const encodeEither: <A, I>(
  codec: Codec<A, I>,
) => (a: A) => Either.Either<I, ParseResult.ParseError> = (codec) => (a) =>
  Either.mapLeft(codec.encode(a), ParseResult.parseError);

// Renamed exports
export {
  Array$ as Array,
  Boolean$ as Boolean,
  Number$ as Number,
  String$ as String,
};
